import * as React from "react";
import { useFormBag } from "react-yup";
import { ValidationError } from "./ValidationError";

import classNames from "classnames";
import { HelpText, HelpTextProps } from "./HelpText";

type LabelProps = React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>;

export interface BaseFieldProps {
  id: string;
  name: string;
  label: string;
  labelProps?: LabelProps;
  helpText?: HelpTextProps["helpText"];
  optional?: boolean;
  wrapperProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  >;
  isInvalid?: boolean;
}

interface PureFieldProps extends FieldProps {
  error?: React.ReactNode;
  touched?: boolean;
}

export const PureField: React.FC<PureFieldProps> = React.memo(
  ({
    id,
    name,
    label,
    labelProps,
    value,
    optional,
    error,
    touched,
    helpText,
    autoComplete,
    className,
    "aria-describedby": ariaDescribedBy,
    ...rest
  }) => {
    return (
      <div className={classNames("nsw-form__group", className)}>
        <label className="nsw-form__label mb-0" htmlFor={id} {...labelProps}>
          {label} {optional ? <small>(optional)</small> : "*"}
        </label>
        {helpText && (
          <HelpText
            id={`${id}-help-text`}
            helpText={helpText}
            className="nsw-form__helper"
          />
        )}
        <input
          id={id}
          className={classNames("nsw-form__input", {
            "is-invalid": error && touched,
          })}
          name={name}
          value={value}
          autoComplete={autoComplete}
          aria-required={!optional}
          aria-invalid={!!error && touched}
          aria-describedby={[
            ariaDescribedBy,
            error && touched ? `${id}-error-message` : null,
            helpText ? `${id}-help-text` : null,
          ]
            .filter((x) => x != null)
            .join(" ")}
          {...rest}
        />
        {error && touched && (
          <ValidationError errorId={`${id}-error-message`} message={error} />
        )}
      </div>
    );
  }
);

type FieldPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  "id" | "name"
>;

interface FieldProps extends FieldPropsOmit, BaseFieldProps {}

export const Field: React.FC<FieldProps> = (props) => {
  const { name } = props;
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <PureField
      {...props}
      value={value as string}
      error={error}
      touched={touched}
    />
  );
};

interface SplitFieldProps extends PureFieldProps {
  append: JSX.Element | null;
}

export const SplitField: React.FC<SplitFieldProps> = ({
  id,
  name,
  label,
  labelProps,
  value,
  optional,
  error,
  touched,
  helpText,
  autoComplete,
  append,
  "aria-describedby": ariaDescribedBy,
  ...rest
}) => {
  return (
    <div className="nsw-form__group">
      <label className="nsw-form__label" htmlFor={id} {...labelProps}>
        {label} {optional ? <small>(optional)</small> : "*"}
      </label>
      {helpText && (
        <HelpText
          id={`${id}-help-text`}
          helpText={helpText}
          className="nsw-form__helper"
        />
      )}
      <div
        className="input-group flex-nowrap"
        css={`
          input {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        `}
      >
        <input
          id={id}
          className={classNames("nsw-form__input", {
            "is-invalid": error && touched,
          })}
          name={name}
          value={value}
          autoComplete={autoComplete}
          aria-required={!optional}
          aria-invalid={!!error && touched}
          aria-describedby={[
            ariaDescribedBy,
            error && touched ? `${id}-error-message` : null,
            helpText ? `${id}-help-text` : null,
          ]
            .filter((x) => x != null)
            .join(" ")}
          {...rest}
        />
        {append}
      </div>
      {error && touched && (
        <ValidationError errorId={`${id}-error-message`} message={error} />
      )}
    </div>
  );
};

type TextAreaPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >,
  "id" | "name"
>;

interface TextAreaProps extends BaseFieldProps, TextAreaPropsOmit {
  className?: string;
}

interface PureTextAreaProps extends BaseFieldProps, TextAreaPropsOmit {
  error?: string;
  touched?: boolean;
}

export const PureTextArea: React.FC<PureTextAreaProps> = ({
  id,
  name,
  label,
  labelProps,
  helpText,
  optional,
  error,
  touched,
  value,
  className,
  ...rest
}) => {
  return (
    <div className={classNames("nsw-form__group", className)}>
      <label className="nsw-form__label mb-0" htmlFor={id} {...labelProps}>
        {label} {optional ? <small>(optional)</small> : "*"}
      </label>
      {helpText && (
        <HelpText
          id={`${id}-help-text`}
          helpText={helpText}
          className="nsw-form__helper"
        />
      )}

      <textarea
        id={id}
        className={classNames("nsw-form__input", {
          "is-invalid": error && touched,
        })}
        name={name}
        value={value as string}
        aria-invalid={!!error && touched}
        aria-required={!optional}
        aria-describedby={`${error && touched ? `${id}-error-message ` : ""}${
          helpText ? `${id}-help-text` : ""
        }`}
        {...rest}
      />
      {error && touched && (
        <ValidationError errorId={`${id}-error-message`} message={error} />
      )}
    </div>
  );
};

export const TextArea: React.FC<TextAreaProps> = (props) => {
  const { name } = props;
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <PureTextArea
      {...props}
      value={value as string}
      error={error}
      touched={touched}
    />
  );
};

type CheckboxPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  "id" | "name" | "type" | "ref"
>;
interface CheckboxProps
  extends Omit<BaseFieldProps, "helpText" | "optional" | "labelProps">,
    CheckboxPropsOmit {
  type: "radio" | "checkbox" | "switch";
  inline?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { id, name, type, label, inline = true, wrapperProps, isInvalid, ...rest },
    ref
  ) => {
    return (
      <>
        <input
          ref={ref}
          name={name}
          type={type === "switch" ? "checkbox" : type}
          id={id}
          className={classNames(`nsw-form__${type}-input`, {
            "is-invalid": isInvalid,
          })}
          {...rest}
        />
        <label
          htmlFor={id}
          className={`nsw-form__${type}-label`}
          css={`
            user-select: none;
          `}
        >
          {label}
        </label>
      </>
    );
  }
);

/*
  TODO : This is a special case for when we want to do something on unmount
  Figure out potentially a better way/move to SKV.AdminAU/pages/Opportunity?
*/
interface CheckWithUnmountProps extends CheckboxProps {
  onUnmount: () => void;
}

export const CheckWithUnmount = React.forwardRef<
  HTMLInputElement,
  CheckWithUnmountProps
>(({ onUnmount, ...props }, ref) => {
  React.useEffect(() => {
    return onUnmount;
  }, []);

  return <Checkbox ref={ref} {...props} />;
});
